import React from "react";
import moment from "moment";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import ImagenesTicket from "./ImagenesTicket";
import { nombreDeUsuario } from "./nombreUsuario";
import { SOPORTE_TEMA, TIPOS_INCIDENCIA } from "./fasesSoporte";
import { AyudaEscalaPuntos, nivelDePuntos, tienePuntos } from "./escalaPuntos";

const Campo = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <div
      style={{
        fontSize: 11,
        color: SOPORTE_TEMA.textSecondary,
        textTransform: "uppercase",
        letterSpacing: 0.3,
        marginBottom: 3,
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 13, color: SOPORTE_TEMA.textPrimary, whiteSpace: "pre-wrap" }}>{children}</div>
  </div>
);

/**
 * Panel de información fija del reporte (no es el chat): empresa/corporativo,
 * quién reportó, descripción original e imágenes adjuntas. Se usa del lado
 * derecho de los modales de detalle de ticket, con el chat del lado izquierdo.
 */
const InfoReporte = ({ ticket, mostrarPasos = true, mostrarNotasInternas = false, perspectivaAgente = false }) => {
  if (!ticket) return null;
  const corporativo = ticket.empresaId?.usuarioId?.nombreCorporativo;
  const nivel = nivelDePuntos(ticket.storyPoints);
  const mostrarPuntos = tienePuntos(ticket) && (perspectivaAgente || ticket.fase === "subido");

  return (
    <div>
      <Campo label="Empresa">
        {ticket.empresaId?.nombre || "Sin empresa (reportado antes de entrar a una empresa)"}
        {corporativo && (
          <div style={{ fontSize: 11, color: SOPORTE_TEMA.textSecondary }}>{corporativo}</div>
        )}
      </Campo>
      <Campo label="Reportó">
        {nombreDeUsuario(ticket.usuarioId)}
        {ticket.usuarioId?.correo && (
          <div style={{ fontSize: 11, color: SOPORTE_TEMA.textSecondary }}>{ticket.usuarioId.correo}</div>
        )}
      </Campo>
      {TIPOS_INCIDENCIA[ticket.tipoIncidencia] && (
        <Campo label="Tipo de incidencia">{TIPOS_INCIDENCIA[ticket.tipoIncidencia]}</Campo>
      )}
      <Campo label="Descripción">{ticket.descripcion || "—"}</Campo>
      {ticket.justificacionRevision && (
        <Campo label="Justificación de la empresa">
          {ticket.justificacionRevision}
          {ticket.revisadoPorId?.nombre && (
            <div style={{ fontSize: 11, color: SOPORTE_TEMA.textSecondary }}>
              Autorizó {nombreDeUsuario(ticket.revisadoPorId)}
              {ticket.fechaRevision ? ` · ${moment(ticket.fechaRevision).format("DD/MM/YYYY HH:mm")}` : ""}
            </div>
          )}
        </Campo>
      )}
      {ticket.motivoNoEnvio && (
        <Campo label="Comentario de tu empresa">
          {ticket.motivoNoEnvio}
          {ticket.revisadoPorId?.nombre && (
            <div style={{ fontSize: 11, color: SOPORTE_TEMA.textSecondary }}>
              Revisó {nombreDeUsuario(ticket.revisadoPorId)}
            </div>
          )}
        </Campo>
      )}
      {/* Puntos que capturó el dev en Jira. */}
      {mostrarPuntos && (
        <Campo label="Complejidad (Jira)">
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 20, fontWeight: 700, lineHeight: 1, color: SOPORTE_TEMA.primario }}>
              {ticket.storyPoints}
            </span>
            <span style={{ fontSize: 12, color: SOPORTE_TEMA.textSecondary }}>
              {ticket.storyPoints === 1 ? "punto" : "puntos"}
            </span>
            {nivel ? (
              <span style={{ fontSize: 13, fontWeight: 600 }}>{nivel.titulo}</span>
            ) : (
              <span style={{ fontSize: 12, color: "#fa8c16" }}>fuera de la escala 1-21</span>
            )}
            <Tooltip title={<AyudaEscalaPuntos />} overlayStyle={{ maxWidth: 360 }}>
              <InfoCircleOutlined style={{ color: SOPORTE_TEMA.textSecondary, cursor: "help" }} />
            </Tooltip>
          </div>
          {nivel && (
            <div style={{ fontSize: 11, color: SOPORTE_TEMA.textSecondary, marginTop: 3 }}>{nivel.detalle}</div>
          )}
          {(typeof ticket.storyPointsEstimado === "number" || ticket.storyPointsFecha) && (
            <div style={{ fontSize: 11, color: SOPORTE_TEMA.textSecondary, marginTop: 3 }}>
              {typeof ticket.storyPointsEstimado === "number" &&
                ticket.storyPointsEstimado !== ticket.storyPoints && <>estimado {ticket.storyPointsEstimado} · </>}
              {ticket.storyPointsFecha &&
                `actualizado ${moment(ticket.storyPointsFecha).format("DD/MM/YYYY HH:mm")}`}
            </div>
          )}
        </Campo>
      )}
      {ticket.motivoRechazo && <Campo label="Comentario de soporte">{ticket.motivoRechazo}</Campo>}
      {ticket.respuestaCliente && (
        <Campo label="¿Se cumplió?">
          {ticket.respuestaCliente === "si" ? "Sí, el cliente lo confirmó" : "Requiere un ajuste adicional, según el cliente"}
          {ticket.fechaRespuestaCliente && (
            <div style={{ fontSize: 11, color: SOPORTE_TEMA.textSecondary }}>
              {moment(ticket.fechaRespuestaCliente).format("DD/MM/YYYY HH:mm")}
            </div>
          )}
        </Campo>
      )}
      {mostrarPasos && ticket.pasosReproduccion && (
        <Campo label="Pasos de reproducción">{ticket.pasosReproduccion}</Campo>
      )}
      {ticket.urlVista && (
        <Campo label="Reportado desde">
          <a href={ticket.urlVista} target="_blank" rel="noreferrer" style={{ fontSize: 12, wordBreak: "break-all" }}>
            {ticket.urlVista}
          </a>
        </Campo>
      )}
      {ticket.imagenes?.length > 0 && (
        <Campo label="Imágenes adjuntas">
          <ImagenesTicket imagenes={ticket.imagenes} />
        </Campo>
      )}
      {mostrarNotasInternas && ticket.notasInternas?.length > 0 && (
        <Campo label="Notas internas (Jira)">
          {ticket.notasInternas.map((nota, indice) => (
            <div
              key={indice}
              style={{
                borderLeft: `2px solid ${SOPORTE_TEMA.border}`,
                paddingLeft: 8,
                marginBottom: 8,
                fontSize: 12,
              }}
            >
              {nota.texto}
              <div style={{ fontSize: 10, color: SOPORTE_TEMA.textSecondary }}>
                {nota.autor || "Jira"}
                {nota.fecha ? ` · ${moment(nota.fecha).format("DD/MM/YYYY HH:mm")}` : ""}
              </div>
            </div>
          ))}
        </Campo>
      )}
    </div>
  );
};

export default InfoReporte;
